import { Directive, inject, Input, OnChanges, OnInit, SimpleChanges, Type } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { NotificationService } from '@app/core/services/notification.service';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { ModalSize } from '@app/shared/models/modal-size';
import { PortalError } from '@app/utils/error/portal-error';
import { FormMappingModelPrototype } from '@app/utils/form-mapping/models/form-mapping-model';
import { loggerFor } from '@app/utils/logger';
import { UpdateFeatureInput } from '@business/feature/components/features/update-feature.component';
import { FeatureService } from '@business/feature/feature.service';
import { BasicFeatureForm } from '@business/feature/models/basic-feature-form';
import { Feature } from '@business/feature/models/feature-model';
import { FeaturesViewModel } from '@business/feature/models/features-view-model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { FormGroupTyped } from 'src/types/typed-forms';

@UntilDestroy()
@Directive()
export abstract class FeatureComponent<FEATURE_MODEL extends Feature = Feature, FORM_MODEL = BasicFeatureForm>
  implements OnChanges, OnInit
{
  @Input()
  allowCallDisplay: boolean = true;

  @Input()
  allowDistribution: boolean = true;

  @Input()
  disabled: boolean = false;

  @Input()
  placeholder?: string;

  @Input()
  featuresViewModel!: FeaturesViewModel;

  @Input()
  update: (feature: Feature) => Observable<any>;

  isResidentialUser: boolean = false;

  error: PortalError | null = null;
  feature: FEATURE_MODEL;
  protected logger = loggerFor('FeatureComponent');

  protected authenticationService: AuthenticationService;
  protected dialogService: DialogService;
  protected featureService: FeatureService;
  protected notificationService: NotificationService;

  form!: FormGroupTyped<FORM_MODEL>;
  protected modalSize?: ModalSize;

  protected abstract formModel: FormMappingModelPrototype;
  protected abstract updateComponent?: Type<DialogComponent<UpdateFeatureInput<any>, void>>;

  /**
   * Every time something changes we evaluate if the feature is now different from its initial state and remember this information
   */
  hasChanged: boolean = false;

  /**
   * Indicator if the component is saving
   */
  saving: boolean = false;

  /**
   * Validation status.
   * We use this property because 'form.valid' could change after the view has been checked and this cannot.
   */
  valid: boolean = true;

  /**
   * Store the reset value.
   * This is used to compare the submitted value (if it's the same we don't submit it).
   */
  protected resetValue: Partial<FORM_MODEL>;

  constructor() {
    /**
     * Manually retrieve the dependencies from the injector so that constructor has no dependencies that must be passed in from child
     * Idea from: https://devblogs.microsoft.com/premier-developer/angular-how-to-simplify-components-with-typescript-inheritance/
     */
    this.authenticationService = inject(AuthenticationService);
    this.dialogService = inject(DialogService);
    this.featureService = inject(FeatureService);
    this.notificationService = inject(NotificationService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.featuresViewModel?.isFirstChange()) {
      this.logger.debug('Features updated, refreshing view');

      this.error = null;
      this.feature = this.getFeature();

      this.updateForm();
      this.refreshValidity();
    }
  }

  ngOnInit(): void {
    // If the formModel isn't set then the feature defined the form manually
    if (this.formModel) {
      this.form = this.formModel.formGroup();
    }

    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.logger.debug('Form status changed to', value);

      this.refreshValidity();
    });

    this.feature = this.getFeature();
    this.updateForm();

    if (this.disabled) {
      this.form.disable();
    }
  }

  protected getFormValue(feature: FEATURE_MODEL): FORM_MODEL {
    return this.formModel.from(feature);
  }

  onChanged() {
    this.hasChanged = JSON.stringify(this.resetValue) !== JSON.stringify(this.form.value);

    this.updateValidity();
    this.submit();
  }

  protected refreshValidity() {
    this.valid = this.form.valid || this.form.disabled;
  }

  reset() {
    this.hasChanged = false;

    this.form.patchValue(this.resetValue, {
      emitEvent: false,
    });

    this.form.updateValueAndValidity();
  }

  showAdvancedSettings(modalSize = this.modalSize) {
    const payload = this.buildUpdatePayload();

    this.dialogService
      .open(this.updateComponent, {
        size: modalSize,
      })
      .withInput(payload);
  }

  protected buildUpdatePayload(): UpdateFeatureInput<FEATURE_MODEL> {
    return {
      allowCallDisplay: this.allowCallDisplay,
      disabled: this.disabled,
      feature: this.formModel.merge(this.feature, this.form.getRawValue()),
      featuresViewModel: this.featuresViewModel,
      update: this.update,
    };
  }

  protected submit(): void {
    if (this.form.valid) {
      this.error = null;

      this.logger.info('Submitting valid form');

      const model = this.formModel.merge(this.feature, this.form.getRawValue());

      if (this.update && this.hasChanged) {
        this.saving = true;

        this.update(model)
          .pipe(finalize(() => (this.saving = false)))
          .subscribe(
            (updatedFeature) => {
              this.updateForm(updatedFeature as FEATURE_MODEL);
            },
            (err) => {
              this.error = err;

              this.reset();
            }
          );
      } else {
        this.updateFeature(model);
      }
    } else {
      this.logger.info('Form is invalid, not submitting');
    }
  }

  protected updateFeature(feature: FEATURE_MODEL) {
    this.logger.debug('Checking if the form changed');

    if (this.hasChanged) {
      this.logger.debug('Form changed, submitting', feature);
      this.saving = true;

      this.notificationService
        .showInfoWhileRunning(
          this.featureService.update(feature).pipe(finalize(() => (this.saving = false))),
          'common.entity.feature'
        )
        .subscribe({
          error: (error) => {
            this.notificationService.handleError(error);
            this.error = error;
            this.reset();
          },
          next: () => {
            this.notificationService.showSuccessfulSave('common.entity.feature');
            this.resetValue = this.getFormValue(feature);
            this.updateForm(feature);
          },
        });
    } else {
      this.logger.debug('Form did not change, not submitting');
    }
  }

  protected updateForm(feature: FEATURE_MODEL = this.feature) {
    this.hasChanged = false;
    this.resetValue = this.getFormValue(feature);

    this.form.patchValue(this.resetValue, {
      emitEvent: false,
    });

    this.form.updateValueAndValidity({
      emitEvent: false,
    });

    this.formChanged();
  }

  protected formChanged(): void {}

  protected updateValidity(): void {}

  protected abstract getFeature(): FEATURE_MODEL;
}
